import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../providers/AuthProvider";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("SocialSignInButton");

export type SocialProvider = "google" | "facebook";

interface SocialSignInButtonProps {
  provider: SocialProvider;
  text?: string;
  disabled?: boolean;
}

interface ProviderConfig {
  buttonClasses: string;
  defaultLabelKey: string;
  errorKey: string;
  logLabel: string;
  Icon: () => ReactElement;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <g fill="none" fillRule="evenodd">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </g>
  </svg>
);

const FacebookIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
      fill="#FFFFFF"
    />
  </svg>
);

const baseButtonClasses =
  "inline-flex w-full items-center justify-center gap-3 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const PROVIDER_CONFIGS: Record<SocialProvider, ProviderConfig> = {
  google: {
    buttonClasses: `${baseButtonClasses} border-slate-200 bg-white text-slate-900 shadow-sm hover:bg-slate-50 focus-visible:outline-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700`,
    defaultLabelKey: "auth.signin.google_button",
    errorKey: "auth.signin.google_error",
    logLabel: "Google",
    Icon: GoogleIcon
  },
  facebook: {
    buttonClasses: `${baseButtonClasses} border-transparent bg-[#1877F2] text-white shadow-lg hover:bg-[#1668d5] focus-visible:outline-[#0f5dc9]`,
    defaultLabelKey: "auth.signin.facebook_button",
    errorKey: "auth.signin.facebook_error",
    logLabel: "Facebook",
    Icon: FacebookIcon
  }
};

export function SocialSignInButton({
  provider,
  text,
  disabled = false
}: SocialSignInButtonProps) {
  const { t } = useTranslation();
  const { loginWithGoogle, loginWithFacebook } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const providerConfig = PROVIDER_CONFIGS[provider];
  const loginHandler = useMemo(() => {
    return provider === "google" ? loginWithGoogle : loginWithFacebook;
  }, [loginWithFacebook, loginWithGoogle, provider]);

  const handleClick = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await loginHandler();
    } catch (err) {
      logger.error(`${providerConfig.logLabel} sign-in error:`, err);
      setError(t(providerConfig.errorKey));
      setIsLoading(false);
    }
  };

  const ButtonIcon = providerConfig.Icon;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isLoading}
        className={providerConfig.buttonClasses}
      >
        <ButtonIcon />
        <span>{isLoading ? t("auth.signin.loading") : text ?? t(providerConfig.defaultLabelKey)}</span>
      </button>

      {error ? (
        <div className="text-sm text-rose-600 dark:text-rose-400" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
